import { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import { ScrollArea } from "@edusogno/primitives/scroll-area.js";
import { getInterval } from "@/utils/diploma.ts";
import { SpinnerLoader } from "@/components/ui/Loader.tsx";
import { useModalAction } from "@/contexts/modal/Context.tsx";
import { useCalendarList } from "@/data/diploma.ts";
import { diplomaRequests } from "@/data/static/diploma.ts";
import { Trans, useTranslation } from "react-i18next";
import { useMe, useUserProducts } from "@/data/user";
import { ProductItem, SortedProducts } from "@/types";
import {
  Select,
  SelectItem,
  SelectValue,
  SelectTrigger,
  SelectContent,
} from "@/components/ui/Select.tsx";
import dayjs, { Dayjs } from "dayjs";
import Button from "@edusogno/primitives/button";
import HourPicker from "@edusogno/composites/hours";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import PdfDiploma from "./PdfDiploma";
import html2canvas from "html2canvas";
import WarningIcon from "@/components/icons/book-exam/WarningIcon.tsx";
import LoadingButton from "@edusogno/primitives/loading-button";
import diplomaBlurred from "@/assets/img/diploma-blurred.png";
import CertificateIcon from "@/components/icons/CertificateIcon.tsx";
import CustomDatePicker from "@edusogno/composites/calendar";
import PdfDiplomaPreview from "@/components/diploma-requests/PdfDiplomaPreview.tsx";
import useDiplomaRequest from "@/utils/hooks/useDiplomaRequest.ts";

const DesktopView = () => {
  const {
    date: stateDate,
    hour,
    requestedAlready,
    showDownload,
    setState,
    calendar: stateCalendar,
  } = useDiplomaRequest();

  const { me } = useMe();
  const { t, i18n } = useTranslation();
  const { openModal } = useModalAction();
  const { setGlobalState } = useGlobals();

  const user = me?.item.user;

  const [disabled, setDisabled] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [courseName, setCourseName] = useState("");
  const [courseDuration, setCourseDuration] = useState(0);
  const [productOptions, setProductOptions] = useState<SortedProducts[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>();
  const [isDiplomaUnlock, setIsDiplomaUnlock] = useState<boolean>(false);
  const [currentProductTypeof, setCurrentProductTypeof] = useState<
    string | null
  >(null);
  const [isDiplomaRequestUnlock, setIsDiplomaRequestUnlock] = useState(false);

  const { calendar, isLoading: loadingEvents } = useCalendarList({
    default: true,
    interval: getInterval(),
    parse: true,
    isUpselling: true,
    token: user?.token,
  });

  const { products, isLoading } = useUserProducts();

  const { selectedDayEvents, availableDays } = useMemo(() => {
    const selectedDays = calendar?.filter(
      (e) =>
        dayjs(e.date).isSame(stateDate, "day") &&
        !dayjs(e.date).isBefore(dayjs()),
    );
    const daysAvailable: Dayjs[] = [];
    calendar?.forEach((el) => {
      const date = dayjs(el.date);
      if (!daysAvailable.includes(date) && !dayjs(date).isBefore(dayjs())) {
        daysAvailable.push(date);
      }
    });

    return {
      selectedDayEvents: selectedDays,
      availableDays: daysAvailable,
    };
  }, [calendar, stateDate]);

  useEffect(() => {
    if (me) {
      if (
        me.item.user.crm_upselling &&
        me.item.user.crm_upselling.meeting_date
      ) {
        setState(
          "date",
          dayjs(me.item.user.crm_upselling?.meeting_date).format(
            "YYYY-MM-DD HH:mm:ss",
          ),
        );
        setState(
          "hour",
          dayjs(
            me.item.user.crm_upselling?.meeting_date,
            "YYYY-MM-DD HH:mm:ss",
          ).format("HH:mm"),
        );
        setState("showDownload", true);
        setState("requestedAlready", true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    if (products && products.items) {
      const groupedProducts = products.items.reduce(
        (acc: { [key: string]: ProductItem[] }, product) => {
          const productCourseName: string = product?.course_info?.course_name;
          if (!acc[productCourseName]) {
            acc[productCourseName] = [];
          }
          acc[productCourseName].push(product);
          return acc;
        },
        {},
      );

      const selectOptions = Object.values(groupedProducts).flatMap((group) => {
        const sortedGroup = group.sort((a: ProductItem, b: ProductItem) => {
          const startDateA = a.pivot?.start_date;
          const startDateB = b.pivot?.start_date;
          if (startDateA && startDateB) {
            return (
              new Date(startDateB).getTime() - new Date(startDateA).getTime()
            );
          } else {
            return 0;
          }
        });

        return sortedGroup.map((product: ProductItem, index) => ({
          label: product?.course_info?.course_name,
          value: index === 0 ? product.id : `${product.id}_1`,
          isNew: index === 0,
          startDate: product?.pivot?.start_date,
          ...product,
        }));
      }) as SortedProducts[];

      setProductOptions(selectOptions);
      const productSelected = selectOptions.find((option) => option.isNew);
      const selectedProductString = JSON.stringify(productSelected);
      setSelectedProduct(selectedProductString);
    }
  }, [products]);

  useEffect(() => {
    if (me && products && products.items) {
      // const upselling = Boolean(user?.crm_upselling);

      const currentProduct = selectedProduct
        ? (JSON.parse(selectedProduct) as ProductItem)
        : null;

      const excludePaths =
        !currentProduct?.product_tag
          ?.toLowerCase()
          .includes("lezioni individuali") ||
        !currentProduct?.product_tag?.toLowerCase().includes("ielts") ||
        false;

      // setIsDiplomaRequestUnlock(upselling || !excludePaths);

      let currentTypeof: string = "pot";

      if (currentProduct) {
        const startDateCourse = currentProduct?.pivot?.end_date;

        setStartDate(dayjs(startDateCourse).format("DD MMMM YYYY"));

        const endDateCourse = currentProduct?.pivot?.end_date;

        if (endDateCourse) {
          const subDays = dayjs(endDateCourse).subtract(21, "d");
          const now = dayjs();

          setIsDiplomaRequestUnlock(subDays <= now || !excludePaths);
        }

        const productInfo = currentProduct?.course_info
          ? currentProduct?.course_info
          : null;
        setCourseName(productInfo?.course_name || "");

        let sum = 0;
        let sumIelts = 0;

        const courseStructure = productInfo?.course_structure;

        courseStructure?.forEach((element) => {
          sumIelts += element.ielts;
          if (sumIelts > 0) {
            currentTypeof = "ielts";
          }

          sum +=
            element?.ielts +
            element?.grammar +
            element?.speaking +
            element?.one_to_one;
        });

        setCourseDuration(sum);
      }

      setCurrentProductTypeof(currentTypeof);
      setIsDiplomaUnlock(Boolean(currentProduct?.pivot?.diploma_unlocked));
    }
  }, [products, me, user, selectedProduct]);

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-background");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportPdf = async () => {
    setDisabled(true);
    const template = document.getElementById("captureDiplomaPDF");
    if (!template) return;
    const canvas = await html2canvas(template);
    const img = canvas.toDataURL("image/png");
    const width = 297;
    const height = width * (9 / 16);
    const doc = new jsPDF("l", "mm", [width, height]);
    doc.addImage(img, "PNG", 0, 0, width, height, "someAlias", "FAST");
    doc.save(user?.first_name?.split(" ")[0].toLowerCase() + "-diploma.pdf");
    setDisabled(false);
  };

  return (
    <div className="w-full h-[--content-height] flex flex-row gap-4">
      <div className="h-full w-full flex flex-col">
        {showDownload ? (
          <div className="w-full h-full grid gap-4 items-start">
            <div className="flex flex-row items-center justify-between">
              <h6 className="flex-shrink-0 mr-4 text-white flex-2">
                {t("diploma_request.select_path")}
              </h6>

              <Select
                value={selectedProduct}
                onValueChange={(value) => setSelectedProduct(value)}
              >
                <SelectTrigger
                  showIcon={false}
                  className="flex items-center w-full text-sm border-0 bg-brand-deep text-light"
                >
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>

                <SelectContent>
                  {productOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={JSON.stringify(option)}
                    >
                      <div className={"flex items-center justify-between"}>
                        <span>{option.label}</span>

                        {option.isNew && (
                          <span className="float-right px-2 text-xs text-white rounded bg-brand-secondary">
                            {t("diploma_request.new")}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-full h-full overflow-hidden">
              {isDiplomaUnlock ? (
                <>
                  <PdfDiplomaPreview
                    user={user}
                    type={currentProductTypeof}
                    start_month={startDate}
                    english_level={user?.english_level || ""}
                    english_course={courseName}
                    course_duration={courseDuration}
                  />

                  <div className="opacity-0 pointer-events-none">
                    <PdfDiploma
                      user={user}
                      type={currentProductTypeof}
                      start_month={startDate}
                      english_level={user?.english_level || ""}
                      english_course={courseName}
                      course_duration={courseDuration}
                    />
                  </div>
                </>
              ) : (
                <img
                  src={diplomaBlurred}
                  alt="blurred diploma"
                  className="object-contain object-center max-h-[190mm] h-full w-full"
                />
              )}
            </div>
            <div className="flex flex-row items-center gap-x-2 justify-center font-normal">
              <i className="shrink-0">
                <WarningIcon className="fill-brand-secondary w-6 h-auto" />
              </i>

              <p className="text-white">{t("diploma_request.share_goals")}</p>
            </div>

            <div className="flex flex-row items-center gap-6 justify-center w-full">
              <LoadingButton
                size="lg"
                loading={disabled}
                onClick={() => void exportPdf()}
                disabled={!isDiplomaUnlock || disabled || !selectedProduct}
              >
                {t("btns.download")}
              </LoadingButton>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex-col justify-between items-center flex bg-white rounded-md py-8 px-4 ">
            <div className="flex flex-col gap-1 w-full">
              <h2 className="text-xl font-semibold text-primary font-[Inter]">
                {t("diploma_request.choose_date_time")}
              </h2>

              <p className={"text-base text-primary"}>
                {t("diploma_request.calendar_invite")}
              </p>
            </div>

            {loadingEvents ? (
              <div className="flex items-center justify-center w-full h-full">
                <SpinnerLoader className={"!h-10 !w-10"} />
              </div>
            ) : (
              <>
                <div className="flex flex-col xl:flex-row w-full py-4 gap-8 justify-center xl:mt-10">
                  <div className="flex-grow h-full gap-x-4">
                    <CustomDatePicker
                      openDates={availableDays}
                      lang={i18n.language as "en" | "it" | "es" | "fr" | "de"}
                      onValueChange={(value) => {
                        if (value) {
                          setState("date", value as string);
                          setState("hour", undefined);
                        }
                      }}
                      minDate={dayjs()}
                      value={stateDate!}
                      type="single"
                      className="w-full"
                    />
                  </div>

                  <div className="w-full flex-1 overflow-x-hidden max-h-[50cqh] overflow-y-auto">
                    <HourPicker
                      type="single"
                      value={hour!}
                      openHours={selectedDayEvents?.map((cal) =>
                        dayjs(cal.date).format("HH:mm"),
                      )}
                      hours={selectedDayEvents?.map((cal) =>
                        dayjs(cal.date).format("HH:mm"),
                      )}
                      onValueChange={(value) => {
                        if (value) {
                          // find the calendar event
                          const event = selectedDayEvents?.find(
                            (cal) =>
                              dayjs(cal.date).format("HH:mm") ===
                              value.toString(),
                          );
                          setState("hour", value as string);
                          setState("calendar", event);
                        }
                      }}
                    />
                  </div>
                </div>

                <Button
                  size="lg"
                  color="green"
                  variant="contained"
                  disabled={!stateDate || !hour}
                  onClick={() =>
                    openModal(
                      "CONFIRM_DIPLOMA_REQUEST_MODAL",
                      {
                        date: stateDate,
                        hour,
                        calendar: stateCalendar,
                        requestedAlready,
                        showDownload,
                      },
                      {
                        renderHeader: (
                          <div className="flex items-center px-2 h-10 bg-brand-secondary rounded-t-md">
                            <h2 className="text-sm font-semibold text-left text-light">
                              {t("diploma_request.header")}
                            </h2>
                          </div>
                        ),
                      },
                    )
                  }
                >
                  {t("btns.confirm")}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="w-full flex-col justify-between gap-y-5 items-center flex bg-light rounded-md py-8 px-2 h-full">
          <div className="flex flex-col items-center">
            <CertificateIcon className="w-10 min-h-10 stroke-brand-secondary block" />

            <h2 className="text-lg font-semibold text-center text-brand">
              {t("diploma_request.how_to_get")}
              <br className="2xl:hidden" /> {t("diploma_request.the_diploma")}
            </h2>
          </div>

          <ScrollArea>
            <div className="flex flex-col gap-y-6 justify-center">
              {diplomaRequests.map((r, idx) => (
                <div
                  key={"step" + idx}
                  className="bg-brand-lavender flex-col flex items-center justify-center border-2 rounded-md gap-2 border-solid border-brand-secondary w-full px-2 py-3"
                >
                  <div className="flex items-center justify-center rounded bg-brand-secondary px-4 py-0.5">
                    <p className="font[Inter] text-xs font-semibold text-light">
                      {t("diploma_request.step")} {idx + 1}
                    </p>
                  </div>

                  <p className="text-brand text-center lg:text-[12px] md:text-[8px]">
                    <Trans
                      i18nKey={r}
                      components={{
                        b_anchor: <b className="font-semibold" />,
                      }}
                    />
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>

          <LoadingButton
            size="md"
            loading={isLoading}
            disabled={
              isDiplomaRequestUnlock ? isLoading : !isDiplomaRequestUnlock
            }
            onClick={() => {
              if (!requestedAlready) {
                setState("showDownload", false);
              } else if (user?.crm_upselling?.meeting_date) {
                openModal(
                  "REQUEST_DIPLOMA_NOTIFICATION_MODAL",
                  {
                    hour,
                    showDownload,
                    requestedAlready,
                    date: stateDate,
                    calendar: stateCalendar,
                  },
                  {
                    renderHeader: (
                      <div className="flex items-center px-2 h-10 bg-brand-secondary rounded-t-md">
                        <h2 className="text-sm font-semibold text-left text-light">
                          {t("diploma_request.header")}
                        </h2>
                      </div>
                    ),
                  },
                );
              }
            }}
          >
            {t("btns.request_diploma")}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

export default DesktopView;
